import { useState } from "react";
import { SHOWCASE } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { breadcrumbJsonLd } from "@/components/Breadcrumb";
import { PageHero } from "@/components/PageHero";
import { ImageSlot } from "@/components/ImageSlot";
import { BeforeAfterSlider } from "@/components/home/BeforeAfterSlider";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/home/CtaBand";

const BREADCRUMB = [
  { label: "Home", to: "/" },
  { label: "Our Work", to: "/gallery" },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "extensions", label: "Extensions" },
  { id: "lofts", label: "Lofts" },
  { id: "kitchens", label: "Kitchens" },
  { id: "bathrooms", label: "Bathrooms" },
  { id: "plastering", label: "Plastering" },
  { id: "refurbishments", label: "Refurbishments" },
];

const PROJECTS = [
  { id: "gallery-rear-extension-thames-ditton.webp", cat: "extensions", caption: "Rear extension, Thames Ditton" },
  { id: "gallery-two-storey-extension-kingston.webp", cat: "extensions", caption: "Two-storey extension, Kingston" },
  { id: "gallery-loft-conversion-surbiton.webp", cat: "lofts", caption: "Loft conversion, Surbiton" },
  { id: "gallery-dormer-loft-conversion-kingston.webp", cat: "lofts", caption: "Dormer loft conversion, Kingston" },
  { id: "gallery-fitted-kitchen-molesey.webp", cat: "kitchens", caption: "Fitted kitchen, Molesey" },
  { id: "gallery-kitchen-refurbishment-esher.webp", cat: "kitchens", caption: "Kitchen refurbishment, Esher" },
  { id: "gallery-family-bathroom-esher.webp", cat: "bathrooms", caption: "Family bathroom, Esher" },
  { id: "gallery-en-suite-surbiton.webp", cat: "bathrooms", caption: "En-suite, Surbiton" },
  { id: "gallery-skimmed-ceiling-kingston.webp", cat: "plastering", caption: "Skimmed ceilings, Kingston" },
  { id: "gallery-rendered-exterior-west-molesey.webp", cat: "plastering", caption: "Rendered exterior, West Molesey" },
  { id: "gallery-full-refurbishment-kingston.webp", cat: "refurbishments", caption: "Full refurbishment, Kingston" },
  { id: "gallery-terrace-refurbishment-surbiton.webp", cat: "refurbishments", caption: "Terrace refurbishment, Surbiton" },
];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const shown = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.cat === filter);

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Our Work | DMS Home Improvements"
        description="A gallery of extensions, loft conversions, kitchens, bathrooms, plastering and refurbishments by DMS Home Improvements across Molesey, Kingston and Surrey."
        jsonLd={breadcrumbJsonLd(BREADCRUMB)}
      />
      <PageHero
        eyebrow="Our Work"
        h1="Our Work"
        intro="Extensions, loft conversions, kitchens, bathrooms, plastering and full refurbishments across Molesey, Kingston and Surrey. Filter by the type of work you are planning."
        breadcrumb={BREADCRUMB}
      />

      <section data-testid="gallery-grid-section" className="bg-white pb-16 md:pb-20">
        <div className="container-site">
          <div data-testid="gallery-filters" className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                data-testid={`gallery-filter-${f.id}`}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
                className={`rounded-btn border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  filter === f.id ? "border-brand bg-brand text-white" : "border-line text-navy hover:border-brand hover:text-brand"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-8">
            {shown.map((p, i) => (
              <Reveal as="li" key={p.id} delay={(i % 3) * 60} data-testid={`gallery-card-${p.cat}`}>
                <figure className="card overflow-hidden">
                  <ImageSlot label={p.id} ratio="4:3" alt={`${p.caption} by DMS Home Improvements`} />
                  <figcaption className="p-5 text-base font-semibold text-navy">{p.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section data-testid="gallery-before-after" aria-labelledby="gallery-ba-heading" className="section section-alt">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2 id="gallery-ba-heading">Before &amp; After</h2>
            <p className="sub-line mt-4 text-body">The clearest way to see the difference. Each pair must show the same property from the same position.</p>
          </Reveal>
          <div className="mt-12 grid gap-10 md:grid-cols-1 lg:gap-12">
            {SHOWCASE.map((item, i) => (
              <Reveal key={item.id} delay={i * 60}>
                <BeforeAfterSlider {...item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
